import React, { useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

export default function ServiceManager() {
  const { services, addService, updateService, deleteService } = useAppContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    duration: '',
    level: 'beginner',
    maxStudents: '1',
    available: true,
  });

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      duration: '',
      level: 'beginner',
      maxStudents: '1',
      available: true,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleEdit = (service) => {
    setFormData({
      title: service.title,
      description: service.description || '',
      price: service.price,
      duration: service.duration || '',
      level: service.level || 'beginner',
      maxStudents: service.maxStudents || '1',
      available: service.available,
    });
    setEditingId(service.id);
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.price) {
      toast.error('Title and price are required');
      return;
    }

    try {
      const serviceData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        duration: formData.duration,
        level: formData.level,
        maxStudents: parseInt(formData.maxStudents),
        available: formData.available,
      };

      if (editingId) {
        await updateService(editingId, serviceData);
        toast.success('Service updated');
      } else {
        await addService(serviceData);
        toast.success('Service added');
      }

      resetForm();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await deleteService(id);
        toast.success('Service deleted');
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete');
      }
    }
  };

  const handleToggleAvailable = async (service) => {
    try {
      await updateService(service.id, { available: !service.available });
      toast.success('Availability updated');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      {isEditing && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle>{editingId ? 'Edit Service' : 'Add New Service'}</CardTitle>
            <Button variant="ghost" size="icon" onClick={resetForm}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Service Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Surfing Lesson"
                    required
                  />
                </div>
                <div>
                  <Label>Price (MAD) *</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="1"
                    step="0.5"
                  />
                </div>
                <div>
                  <Label>Level</Label>
                  <Select value={formData.level} onValueChange={(val) => setFormData({ ...formData, level: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Students</Label>
                  <Input
                    type="number"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({ ...formData, maxStudents: e.target.value })}
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div>
                  <Label>Availability</Label>
                  <Select value={formData.available ? 'available' : 'unavailable'} onValueChange={(val) => setFormData({ ...formData, available: val === 'available' })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="unavailable">Unavailable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Service description..."
                  className="w-full p-2 border rounded-md"
                  rows="3"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingId ? 'Update Service' : 'Add Service'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {!isEditing && (
        <Button onClick={() => setIsEditing(true)} className="w-full">
          + Add New Service
        </Button>
      )}

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Availability</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services && services.length > 0 ? (
              services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>{service.title}</TableCell>
                  <TableCell>{service.price} MAD</TableCell>
                  <TableCell>{service.level || 'N/A'}</TableCell>
                  <TableCell>{service.duration ? service.duration + ' hrs' : 'N/A'}</TableCell>
                  <TableCell>
                    <Button
                      variant={service.available ? 'default' : 'secondary'}
                      size="sm"
                      onClick={() => handleToggleAvailable(service)}
                    >
                      {service.available ? 'Available' : 'Unavailable'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(service)}>
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(service.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan="6" className="text-center text-gray-500 py-4">
                  No services yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}